import axios from "./axios";
import type { AxiosRequestHeaders } from "axios";
import {
  getEmbeddedState,
  getToken,
  shopifyHeaders,
} from "../../utils/app-bridge/app-bridge-helpers";
import { isLocalEnv } from "../../utils";
import configService from "../config/config-service";
import { localPrefixUrl } from "./api-urls";

interface KeyValue {
  [name: string]: string | number;
}
interface IGetParams {
  withAuth?: boolean;
  useDummyToken?: boolean;
  url: string;
  headers?: AxiosRequestHeaders | null;
  queryParams?: Record<string, unknown>;
  responseType?:
    | "arraybuffer"
    | "blob"
    | "document"
    | "json"
    | "text"
    | "stream";
  signal?: AbortSignal;
}

interface IPostParams extends IGetParams {
  data?: Record<string, unknown> | FormData | KeyValue;
}

interface IInjectTokenInHeaders {
  withAuth?: boolean;
  useDummyToken?: boolean;
  headers?: Record<string, string> | null;
}

const injectTokenInHeaders = async (options?: IInjectTokenInHeaders) => {
  let headers: Record<string, string> = options?.headers || {};

  // inject shopifyHeaders on local instance only
  if (isLocalEnv) {
    headers = { ...headers, ...shopifyHeaders };
  }
  // don't inject the token if no withAuth
  if (!options?.withAuth) {
    return headers;
  }
  const token = await getToken(options?.useDummyToken);

  return {
    ...headers,
    Authorization: `Bearer ${token}`,
  };
};

export const httpGet = async (params: IGetParams) => {
  const {
    url,
    withAuth = true,
    useDummyToken = false,
    headers = {},
    queryParams = {},
    responseType,
    signal,
  } = params;
  const injectedHeaders = await injectTokenInHeaders({
    withAuth,
    useDummyToken,
    headers,
  });

  return axios.get(url, {
    ...(queryParams && { params: queryParams }),
    ...(injectedHeaders && { headers: injectedHeaders }),
    ...(responseType && { responseType }),
    ...(signal && { signal }),
  });
};

export const httpPost = async (params: IPostParams) => {
  const {
    withAuth = true,
    useDummyToken = false,
    url,
    data = {},
    headers = {},
    queryParams = {},
    responseType,
    signal,
  } = params;
  const injectedHeaders = await injectTokenInHeaders({
    withAuth,
    useDummyToken,
    headers,
  });

  return axios.post(url, data, {
    params: { ...queryParams },
    ...(injectedHeaders && { headers: injectedHeaders }),
    ...(responseType && { responseType }),
    ...(signal && { signal }),
  });
};

export const httpPut = async (putParams: IPostParams) => {
  const {
    withAuth = true,
    useDummyToken = false,
    url,
    data = {},
    headers = {},
    queryParams = {},
    responseType,
    signal,
  } = putParams;
  const injectedHeaders = await injectTokenInHeaders({
    withAuth,
    useDummyToken,
    headers,
  });

  return axios.put(url, data, {
    params: { ...queryParams },
    ...(injectedHeaders && { headers: injectedHeaders }),
    ...(responseType && { responseType }),
    ...(signal && { signal }),
  });
};

export const httpDelete = async (deleteParams: IGetParams) => {
  const {
    withAuth = true,
    useDummyToken = false,
    url,
    headers = {},
    queryParams = {},
    signal,
  } = deleteParams;
  const injectedHeaders = await injectTokenInHeaders({
    withAuth,
    useDummyToken,
    headers,
  });

  return axios.delete(url, {
    ...(headers && {
      ...(injectedHeaders && { headers: injectedHeaders }),
    }),
    params: { ...queryParams },
    ...(signal && { signal }),
  });
};

// local - use relative API urls to call direct BE service url configured in (.env.local)
// embedded instance - use APIG base API urls configured in app-config.json (docker env vars)
export const setAxiosBaseUrl = () => {
  const { isEmbedded } = getEmbeddedState(
    configService.appConfig.shopifyApiKey,
  );
  if (isEmbedded) {
    axios.defaults.baseURL = `https://${configService.appConfig.apiHost}${configService.appConfig.apiBasePath}`;
  } else {
    axios.defaults.baseURL = localPrefixUrl;
  }
};
